// src/context/WorkspaceContext.tsx
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "@/context/AuthContext";
import type { WorkspaceSummary } from "@/types/auth";

interface WorkspaceContextType {
  workspaces: WorkspaceSummary[];
  activeWorkspaceId: string | null;
  activeWorkspace: WorkspaceSummary | null;
  switchWorkspace: (workspaceId: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  workspaces: [],
  activeWorkspaceId: null,
  activeWorkspace: null,
  switchWorkspace: () => {},
});

// v2: bumped when the account-wide COMPANY workspace became the default landing
// context. The version bump deliberately ignores any pre-existing "ws-active-*"
// pin so every session lands in COMPANY once on first load after deploy; explicit
// switches afterward persist under the v2 key. See plan
// 2026-06-04-company-wide-default-workspace.md (Phase 4).
const STORAGE_KEY_PREFIX = "ws-active-v2-";

function getStorageKey(accountId: string): string {
  return `${STORAGE_KEY_PREFIX}${accountId}`;
}

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(null);
  const prevAccountIdRef = useRef<string | null>(null);

  const workspaces = user?.workspaces ?? [];
  const accountId = user?.activeAccount?.id ?? null;

  // Resolve the initial workspace ID for a given account
  const resolveWorkspaceId = useCallback(
    (acctId: string, wsList: WorkspaceSummary[]): string | null => {
      // 1. Explicit prior choice (account-scoped localStorage)
      const stored = localStorage.getItem(getStorageKey(acctId));
      if (stored && wsList.some((ws) => ws.id === stored)) {
        return stored;
      }

      // 2. The account-wide COMPANY workspace is the default landing context.
      const company = wsList.find((ws) => ws.type?.toUpperCase() === "COMPANY");
      if (company) {
        return company.id;
      }

      // 3. Fall back to defaultWorkspaceId (only reached when COMPANY is absent,
      //    e.g. pre-backfill)
      if (user?.defaultWorkspaceId && wsList.some((ws) => ws.id === user.defaultWorkspaceId)) {
        return user.defaultWorkspaceId;
      }

      // 4. First workspace
      if (wsList.length > 0) {
        return wsList[0].id;
      }

      // 5. No workspaces
      return null;
    },
    [user?.defaultWorkspaceId]
  );

  // Initialize workspace on user load + reset on account switch
  useEffect(() => {
    if (!accountId) {
      setActiveWorkspaceId(null);
      prevAccountIdRef.current = null;
      return;
    }

    const isAccountSwitch = prevAccountIdRef.current !== null && prevAccountIdRef.current !== accountId;
    prevAccountIdRef.current = accountId;

    if (isAccountSwitch || activeWorkspaceId === null) {
      const resolved = resolveWorkspaceId(accountId, workspaces);
      setActiveWorkspaceId(resolved);
    } else {
      // Validate current workspace still exists in the workspace list
      if (!workspaces.some((ws) => ws.id === activeWorkspaceId)) {
        const resolved = resolveWorkspaceId(accountId, workspaces);
        setActiveWorkspaceId(resolved);
      }
    }
  }, [accountId, workspaces, resolveWorkspaceId]);

  // Persist to localStorage whenever workspace changes
  useEffect(() => {
    if (accountId && activeWorkspaceId) {
      localStorage.setItem(getStorageKey(accountId), activeWorkspaceId);
    }
  }, [accountId, activeWorkspaceId]);

  const switchWorkspace = useCallback(
    (workspaceId: string) => {
      if (workspaceId === activeWorkspaceId) return;
      // Validate the workspace exists in the current list
      if (!workspaces.some((ws) => ws.id === workspaceId)) return;
      setActiveWorkspaceId(workspaceId);
    },
    [activeWorkspaceId, workspaces]
  );

  const activeWorkspace = workspaces.find((ws) => ws.id === activeWorkspaceId) ?? null;

  return (
    <WorkspaceContext.Provider
      value={{
        workspaces,
        activeWorkspaceId,
        activeWorkspace,
        switchWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) throw new Error("useWorkspace must be used within a WorkspaceProvider");
  return context;
};
